import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../layouts/DashboardLayout';
import { api } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Play, Loader2, MonitorPlay } from 'lucide-react';
import { toast } from 'sonner';
import StudentVideoPlayer from '../../components/SimpleVideoPlayer';

// ── Main Student Video Lectures Page ─────────────────────────────────────────
const StudentVideoLecturesPage = () => {
  const [lectures, setLectures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [playingLecture, setPlayingLecture] = useState(null);

  useEffect(() => {
    const fetchLectures = async () => {
      try {
        const res = await api.get('/student/video-lectures');
        setLectures(res.data || []);
      } catch {
        toast.error('Failed to load video lectures');
      } finally {
        setLoading(false);
      }
    };
    fetchLectures();
  }, []);

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <MonitorPlay className="h-6 w-6 text-indigo-600" />
            Video Lectures
          </h1>
          <p className="text-slate-500 text-sm">Watch lectures assigned by your teacher</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
          </div>
        ) : lectures.length === 0 ? (
          <div className="text-center py-20">
            <MonitorPlay className="h-16 w-16 mx-auto text-slate-300 dark:text-slate-600 mb-4" />
            <h3 className="text-lg font-semibold text-slate-500 dark:text-slate-400">No lectures available yet</h3>
            <p className="text-sm text-slate-400 mt-1">Your teacher hasn't assigned any video lectures to your class</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {lectures.map(lecture => (
              <Card key={lecture.id} className="overflow-hidden hover:shadow-lg transition-all cursor-pointer group"
                onClick={() => setPlayingLecture(lecture)}>
                <div className="aspect-video bg-slate-900 relative flex items-center justify-center overflow-hidden">
                  <MonitorPlay className="h-16 w-16 text-white/20 group-hover:text-white/30 transition-colors" />
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/30">
                    <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                      <Play className="h-7 w-7 text-white ml-1" />
                    </div>
                  </div>
                  {lecture.sequence_number && (
                    <span className="absolute top-2 left-2 text-xs font-bold px-2 py-0.5 rounded-full bg-indigo-600 text-white">
                      #{lecture.sequence_number}
                    </span>
                  )}
                </div>
                <CardContent className="p-3">
                  <h3 className="font-semibold text-slate-900 dark:text-white text-sm line-clamp-1">{lecture.title}</h3>
                  {lecture.description && (
                    <p className="text-xs text-slate-500 mt-1 line-clamp-2">{lecture.description}</p>
                  )}
                  <Button size="sm" className="w-full mt-3 bg-indigo-600 hover:bg-indigo-700 gap-2 text-xs">
                    <Play className="h-3.5 w-3.5" /> Watch Now
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {playingLecture && (
        <StudentVideoPlayer
          videoSrc={playingLecture.video_path}
          title={playingLecture.title}
          subtitle={playingLecture.sequence_number ? `Lecture #${playingLecture.sequence_number}` : null}
          allowSeek={true}
          autoPlay={true}
          onVideoEnd={() => setPlayingLecture(null)}
          onClose={() => setPlayingLecture(null)}
        />
      )}
    </DashboardLayout>
  );
};

export default StudentVideoLecturesPage;
